import React, { useEffect, useState } from "react";

const CONTRACTOR_STEPS = [
  {
    number: 1,
    title: "Upload all your raws",
    description:
      "Use Upload to add every photo for this job into one grid. You don’t sort files into folders first — everything lands in Unassigned.",
    tip: "You can upload in batches; keep adding until the full set is here.",
  },
  {
    number: 2,
    title: "Mark each photo M or E",
    description:
      "Each card has M (blue) and E (green). Tap M on the best exposure for a room (Main). Tap E on the darker/brighter brackets of the same room. Click a photo to enlarge before deciding.",
    tip: "For Drone — usually just M. Photo, Dusk & Golden Hour, and Portraits can use E for bracketed exposures.",
  },
  {
    number: 3,
    title: "Tap + Shot to lock in a group",
    description:
      "Once you’ve marked M (and E if needed) for one room, tap + Shot. That group becomes Shot 1. Repeat for the next room until you’re done.",
    tip: "You can add a room name on each shot strip after it’s created.",
  },
  {
    number: 4,
    title: "Switch service tab for drone, dusk, portraits",
    description:
      "Use the service tabs (Photo, Drone, Dusk, Portraits, …) for each type. Finish marking and + Shot on one tab before switching — pending marks clear when you change tabs.",
    tip: "Switching tabs clears unfinished M/E marks so you don’t mix categories.",
  },
  {
    number: 5,
    title: "Anything unmarked goes to Extras",
    description:
      "Photos you didn’t put in a shot stay Unassigned and go to Extras when you Send to Editor. They’re kept for admin — they just aren’t edited as shots.",
    tip: "Use Extras for backups or shots you’re not sure about.",
  },
];

const EDITOR_STEPS = [
  {
    number: 1,
    title: "Every shot arrives processed",
    description:
      "When you open a job, bracketed shots are HDR-merged and AI-edited in the background. Review each card — you don’t need to start merge/edit from scratch.",
    tip: "Status shows AIREADY when AI is done, or PRINT MASTER once you’ve selected a master.",
  },
  {
    number: 2,
    title: "Understand the HDR merge",
    description:
      "HDR combines bracket exposures into one balanced image. If a merge looks wrong, fix exposures and use Re-run HDR merge (or Retry HDR on failure).",
    tip: "Photo, Dusk & Golden Hour, and Portraits show Main + Exposures; Drone/Dusk are single frames.",
  },
  {
    number: 3,
    title: "Use the before/after slider",
    description:
      "Drag the handle to compare Original/HDR vs AI. Click a half to open that image large.",
    tip: "If AI looks off, set Enhancement / Notes and Send to AI edit again.",
  },
  {
    number: 4,
    title: "Add enhancements if needed",
    description:
      "Use Enhancement (None, Declutter, Virtual Furnish) and Notes, then Send to AI edit. Adjust image applies a local preview filter only — it isn’t saved to the server yet.",
    tip: "You can re-run AI as many times as you need.",
  },
  {
    number: 5,
    title: "Mark as print master and submit",
    description:
      "When a shot looks right, Use as print master. Track progress in Edited Images, then submit when print masters are ready.",
    tip: "You can still re-run AI or HDR after marking print master if you change your mind.",
  },
];

/**
 * Step-by-step guide modal — contractor | editor.
 */
export default function HelpGuide({ mode = "contractor", onClose }) {
  const [step, setStep] = useState(0);
  const steps = mode === "editor" ? EDITOR_STEPS : CONTRACTOR_STEPS;
  const current = steps[step];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-[720px] overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="How it works"
      >
        <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-[#F0F0F0]">
          <div>
            <span
              className={`text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-1 rounded-full ${
                mode === "contractor"
                  ? "bg-[#1A1A1A] text-white"
                  : "bg-[#E8E8E8] text-[#3D3D3D]"
              }`}
            >
              {mode === "contractor" ? "Contractor guide" : "Editor guide"}
            </span>
            <h2 className="text-[20px] font-bold text-[#1A1A1A] mt-2 m-0">
              How it works
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#F5F5F5] flex items-center justify-center hover:bg-[#E8E8E8] transition-colors border-0 cursor-pointer"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex gap-0 px-7 pt-4 overflow-x-auto">
          {steps.map((s, i) => (
            <button
              key={s.number}
              type="button"
              onClick={() => setStep(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-[12px] font-semibold transition-all whitespace-nowrap mr-2 mb-2 border-0 cursor-pointer ${
                i === step
                  ? "bg-[#1A1A1A] text-white"
                  : "bg-[#F5F5F5] text-[#6B6B6B] hover:bg-[#E8E8E8]"
              }`}
            >
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  i === step
                    ? "bg-white text-[#1A1A1A]"
                    : "bg-[#E8E8E8] text-[#6B6B6B]"
                }`}
              >
                {s.number}
              </span>
              Step {s.number}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-7 pb-7">
          <div className="flex items-start gap-4 mt-2">
            <div className="w-9 h-9 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center text-[15px] font-bold flex-none">
              {current.number}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[17px] font-bold text-[#1A1A1A] mb-2 mt-0">
                {current.title}
              </h3>
              <p className="text-[14px] text-[#3D3D3D] leading-relaxed mb-3">
                {current.description}
              </p>
              {current.tip && (
                <div className="flex items-start gap-2.5 bg-[#FAFAFA] border border-[#F0F0F0] rounded-xl px-4 py-3">
                  <p className="text-[13px] text-[#6B6B6B] leading-relaxed m-0">
                    <span className="font-semibold text-[#3D3D3D]">Tip: </span>
                    {current.tip}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-7 py-4 border-t border-[#F0F0F0]">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="text-[13px] font-semibold text-[#3D3D3D] hover:text-black transition-colors disabled:opacity-30 border-0 bg-transparent cursor-pointer"
          >
            Previous
          </button>
          <span className="font-mono text-[11px] text-[#C0C0C0]">
            {step + 1} of {steps.length}
          </span>
          {step < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              className="text-[13px] font-semibold text-[#1A1A1A] border-0 bg-transparent cursor-pointer"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="bg-[#1A1A1A] text-white rounded-lg px-5 py-2 text-[13px] font-bold hover:opacity-80 transition-opacity border-0 cursor-pointer"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
